import { isVideoId } from '../links/routes.ts';
import type { ParseError } from '../result.ts';

/**
 * §8's History row — `GET /api/v1/auth/history`, its paging, and the five things measurement said
 * that the documentation did not (measured against `invidious.example.com`).
 *
 * The endpoint returns **a bare array of video id strings**. No titles, no authors, no durations, no
 * thumbnails — so this module is only the *list*, and everything a row displays comes from
 * `history/hydrate.ts`. Splitting them is not tidiness: the list is ~14 bytes per row and the display
 * metadata is 89–262 KB per row, so they are two problems with a thousandfold difference in cost.
 *
 * ## The five measurements
 *
 * **1. A read that names no `max_results` is sized by the account's own preference, server-side.**
 * With 68 rows stored, a bare `GET` returned **40** — and `GET /api/v1/auth/preferences` reports
 * `max_results: 40` for that account. So the default window is not a constant this client can know; it
 * is whatever the reader set in Invidious's own settings, and it would change under us if they changed
 * it. `max_results=abc` falls back to the same preference (the parse fails and the default applies), so
 * a typo is not an error either. {@link historyWireParams} therefore **always sends both parameters**.
 *
 * **2. `max_results=0` and `max_results=-1` answer `200 []`.** Silent, exactly as 3b found for the
 * feed, and byte-identical to "your history is empty" and to "you are past the last page". Clamped.
 *
 * **3. `page=0`, `page=-1` and `page=-2` answer `200` with the *oldest* window.** Not an error, not
 * empty: `?max_results=5&page=0` returned the rows at unpaged indices 23–27 of 28 — the **tail of the
 * list wearing page 1's clothes**. All three values gave the same last five rows. This is the third
 * distinct behaviour for the same parameter in three slices — 2e's playlist answers `200` with
 * `videos: []`, 3b's feed answers `403` with a leaked Postgres `OFFSET must not be negative`, and this
 * one answers plausible data from the wrong end. A page number is the one parameter this API cannot be
 * trusted with, and it is clamped here for all of them.
 *
 * **4. `page=abc` and `page=1e9` answer page 1**, for measurement 1's reason: the integer parse fails
 * and the default applies. So a garbage page is *silently the first page* rather than a refusal — which
 * matters because it means no request can prove it was honoured (§2's first lesson).
 *
 * **5. The list reorders at the head, so offset paging over it re-serves rows.** `POST`ing an id that
 * is already in the history **moves it to index 0** rather than duplicating it (measured: index 27 → 0,
 * length unchanged at 28). So one re-watch mid-read shifts every row down one — measured directly, page
 * 2 re-served 1 of its 5 rows after a single re-mark. Hence {@link foldHistoryPages} dedupes, the same
 * mechanism 3b's feed has for a different cause (that list grows at the head; this one *reorders* at
 * it).
 *
 * What is *not* a problem here, and is worth stating because the sibling endpoints differ: paging is
 * clean offset paging over a stable list. Measured `5·5·5·5·5·3` then `0` over 28 rows — the
 * concatenation is exactly the unpaged read, 28 of 28 distinct, with no intra-page repetition (unlike
 * `/api/v1/playlists/{plid}`, whose window is twice its stride) and two identical requests
 * byte-identical (unlike `/api/v1/search`). And **a short page really is the last page**, as it is for
 * the feed and is not for a channel tab.
 */

export const HISTORY_PATH = '/api/v1/auth/history';

/** Pages are 1-based, and page 0 is not empty — it is the *end* of the list. See this module's note 3. */
export const HISTORY_FIRST_PAGE = 1;

/**
 * 40 — the same number 3b uses, and for once it is the instance's own default rather than a choice: it
 * is what a bare read returned, because it is the account's `max_results` preference.
 *
 * Unlike the feed's, this page size is not a bandwidth decision. 40 ids is ~600 bytes, and the whole
 * 68-row history is ~1 KB, so "just fetch everything" would be cheap *for the list*. It is a **grid**
 * decision: every row drawn is a thumbnail request, and every row scrolled into view is a potential
 * 89–262 KB hydration, so the page size bounds what the reader can spend by scrolling rather than what
 * the response costs.
 */
export const HISTORY_PAGE_SIZE = 40;

/**
 * The wire parameters, with every clamp this endpoint's four silent failures need.
 *
 * **Both parameters are always sent, including on the first page.** Omitting `max_results` does not mean
 * "the default" in any sense this client can rely on — it means "the size the reader configured in
 * Invidious", which is unknowable from here and would silently break {@link nextHistoryPage}'s
 * terminating proof (a 40-row page read as short against an assumed 20 would end the list four rows in).
 */
export function historyWireParams(
  page: number,
  maxResults: number = HISTORY_PAGE_SIZE,
): Readonly<Record<string, string | null>> {
  const safePage = Number.isFinite(page) ? Math.max(HISTORY_FIRST_PAGE, Math.floor(page)) : HISTORY_FIRST_PAGE;
  const safeMax = Number.isFinite(maxResults) ? Math.max(1, Math.floor(maxResults)) : HISTORY_PAGE_SIZE;

  return { max_results: String(safeMax), page: String(safePage) };
}

/**
 * One response: the ids it carried, and what could not be read.
 *
 * `malformed` is separated from "the array was empty" for `parseSubscriptionFeedPage`'s reason: an empty
 * array is a real, frequent and *ambiguous* answer here — a signed-in reader with no history, every page
 * past the last one, and (before the clamp) any request carrying `max_results=0` are all `200 []`, two
 * bytes, indistinguishable. A response that is not an array at all is a different thing entirely.
 */
export interface HistoryPage {
  readonly ids: readonly string[];
  /** R4 — entries dropped rather than lost. See {@link parseHistoryPage} for what earns a drop. */
  readonly dropped: readonly ParseError[];
  readonly malformed: boolean;
}

/**
 * Several pages, folded into the one list the page renders.
 */
export interface HistoryListing {
  readonly ids: readonly string[];
  /** Rows a later page repeated because an earlier one already had them — this module's note 5. */
  readonly duplicates: number;
  readonly dropped: number;
  readonly malformed: boolean;
}

/**
 * Concatenate the pages, keeping the **first** occurrence of an id.
 *
 * The dedupe is earned by a measurement rather than argued from a mechanism, which is the difference
 * from 3b: there the shift was reasoned about and never observed, here it was produced on demand. One
 * `POST` of an id already in the list moved it from the last position to the first, and the next page
 * read then contained a row the previous page had already shown. Since `POST` is exactly what the Watch
 * page does while the reader is watching something, "the list reordered between two page reads" is the
 * ordinary case for this endpoint rather than the exotic one.
 *
 * Keeping the first occurrence is what stops the reader's list rearranging itself under them. The row
 * pushed off the end is lost until a refresh, which is the honest cost of offset paging over live data —
 * `duplicates` is reported rather than swallowed so the page can say so.
 */
export function foldHistoryPages(pages: readonly HistoryPage[]): HistoryListing {
  const seen = new Set<string>();
  const ids: string[] = [];
  let duplicates = 0;
  let dropped = 0;
  let malformed = false;

  for (const page of pages) {
    dropped += page.dropped.length;
    malformed = malformed || page.malformed;
    for (const id of page.ids) {
      if (seen.has(id)) {
        duplicates += 1;
        continue;
      }
      seen.add(id);
      ids.push(id);
    }
  }

  return { ids, duplicates, dropped, malformed };
}

/**
 * The next page to ask for, or `null` when the page that already arrived proved there is nothing after
 * it.
 *
 * Sound here for the same reason it is sound for the feed and unsound for a channel tab: the window is a
 * SQL `LIMIT`/`OFFSET` the request itself specified, so a page shorter than `max_results` means the
 * table ran out. Measured over six pages at 5 — `5·5·5·5·5·3`, then `0`.
 *
 * That earns a real request rather than a guess, and the alternative is visible in the product: a fold
 * that stopped only on an *empty* page would leave every complete history showing a "Load more" button
 * that fetches 2 bytes and adds nothing.
 */
export function nextHistoryPage(
  pages: readonly HistoryPage[],
  maxResults: number = HISTORY_PAGE_SIZE,
): number | null {
  if (pages.length === 0) return HISTORY_FIRST_PAGE;

  const last = pages[pages.length - 1];
  if (last === undefined || last.malformed) return null;
  // Counted before the dedupe and before R4's drops, exactly as the feed's is: the question is how many
  // rows the *instance* put in the window, and an id we refused still filled a slot. Counting survivors
  // would read one bad id as the end of the history.
  const returned = last.ids.length + last.dropped.length;
  if (returned < Math.max(1, Math.floor(maxResults))) return null;

  return HISTORY_FIRST_PAGE + pages.length;
}

/* ------------------------------------------------------------------------------------------------
 * The two writes
 * ---------------------------------------------------------------------------------------------- */

/**
 * ## ⚠️ An empty id does not mean "no id" — it means *the whole history*
 *
 * **`DELETE /api/v1/auth/history` clears the entire watch history, with or without a trailing slash,
 * and answers the same `204` with the same empty body that removing one row answers.** Found the way
 * these things are always found: a probe in this slice sent `DELETE /api/v1/auth/history/` to see how
 * the endpoint refuses an empty id, read the `204` as a harmless no-op, and moved on — and a 68-row
 * history was gone. Re-verified three times afterwards, refilling in between: `/history/`, `/history`
 * and `/history?all=true` each answer `204` and leave zero rows.
 *
 * So a template that interpolates an empty video id turns "remove this one row" into "destroy
 * everything", **with no distinguishable response**. There is no status, no body and no header to notice
 * it by — the successful destruction and the successful single removal are byte-identical.
 *
 * That is why both path builders below return `string | null` rather than a string, and why the
 * deliberate act has a name of its own ({@link CLEAR_HISTORY_PATH}). A caller cannot reach the
 * destructive form by omission; it has to ask for it. The shape check is the same
 * {@link parseHistoryPage} applies on the way in, so the two directions agree by construction.
 */

/**
 * `POST /api/v1/auth/history/{id}` — mark watched. `204`, empty body. `null` for an id that is not one,
 * which keeps a `400` off the wire.
 *
 * **Three measured facts, and each one shapes a caller:**
 *
 * 1. **It does not check that the video exists.** A well-shaped id for no video at all
 *    (`aaaaaaaaaaa`) answers `204` and *lands in the history at the head*, where hydrating it then
 *    answers `404 {"error":"Video unavailable"}`. So a history legitimately contains rows with no video
 *    behind them, and a page that treats an unhydratable row as an error is wrong about a normal state.
 * 2. **It is idempotent-with-a-side-effect: a re-mark moves the row to the head.** So this is "mark
 *    watched", not "record a view" — there is no duplicate to guard against, and the list's order is
 *    *last marked* first rather than first watched.
 * 3. **A malformed id is a `400`, not the `403` a malformed playlist write answers.** The write
 *    endpoints of this API do not agree on how they refuse a bad request, and a bare `400` is fatal for
 *    media (`classifyMediaFailure`) while meaning "our bug" here. An *empty* id is a third thing again:
 *    `403 {"error":"Requested path: 'POST:/api/v1/auth/history/' was not found."}` — a routing miss
 *    dressed as an authorization failure, which is a sixth meaning for that status.
 */
export function markWatchedPath(videoId: string): string | null {
  return isVideoId(videoId) ? `${HISTORY_PATH}/${videoId}` : null;
}

/**
 * `DELETE /api/v1/auth/history/{id}` — remove **one** row. `204`, and idempotent in the strong sense:
 * an id that is not in the history answers `204` too, as does the same id twice. So a row somebody
 * removed in another tab is not an error, and a retry cannot fail differently.
 *
 * `null` for anything that is not a video id — see this section's warning. That is not defensive
 * programming, it is the difference between removing a row and wiping the account's history.
 */
export function removeFromHistoryPath(videoId: string): string | null {
  return isVideoId(videoId) ? `${HISTORY_PATH}/${videoId}` : null;
}

/**
 * `DELETE /api/v1/auth/history` — **clear everything**, in one request.
 *
 * §8 and this repo's own notes had clear-all as "iterate the list", which measurement replaced with a
 * single call. Worth having as a constant with a name rather than as `HISTORY_PATH` at a call site: the
 * two paths differ by one segment, the responses are identical, and the difference in outcome is total.
 *
 * The other half of the finding — that the *same* path is what an empty id produces — is why nothing
 * here builds a removal path by interpolation.
 */
export const CLEAR_HISTORY_PATH = HISTORY_PATH;

/* ------------------------------------------------------------------------------------------------
 * The notes a History page has to make
 * ---------------------------------------------------------------------------------------------- */

/**
 * Something true about this list that a reader cannot see by looking at it. R7: core decides which are
 * true, the platform writes the sentences.
 */
export type HistoryNote =
  /** The fold discarded repeats — something was marked watched while the reader was paging. */
  | { readonly kind: 'shifted'; readonly count: number }
  /** R4 — ids that were not usable as ids. */
  | { readonly kind: 'dropped'; readonly count: number }
  /** The response was not an array at all. */
  | { readonly kind: 'malformed' }
  /** Rows whose video no longer exists, which `POST`'s lack of validation makes an ordinary state. */
  | { readonly kind: 'unavailable'; readonly count: number };

export function historyNotes(listing: HistoryListing, unavailable: number): readonly HistoryNote[] {
  const notes: HistoryNote[] = [];

  if (listing.malformed) notes.push({ kind: 'malformed' });
  if (unavailable > 0) notes.push({ kind: 'unavailable', count: unavailable });
  if (listing.duplicates > 0) notes.push({ kind: 'shifted', count: listing.duplicates });
  // Suppressed under `malformed`, because a response that was not a list also books itself as one
  // dropped entry — and saying "the response was not a list" *and* "1 row could not be read" is one
  // fact reported twice, in a way that reads like two independent problems.
  if (listing.dropped > 0 && !listing.malformed) notes.push({ kind: 'dropped', count: listing.dropped });

  return notes;
}
